/**
 * Trunnion AI - Terminal Node
 * Workflow exit point.
 */

import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { CircleStop, CheckCircle, XCircle, Ban } from 'lucide-react';
import type { TerminalNodeData } from '@/types';
import { useWorkflowDesignerStore } from '@/stores/workflowDesignerStore';

const terminalConfig = {
  success: { icon: CheckCircle, color: 'green', label: 'Success' },
  failure: { icon: XCircle, color: 'red', label: 'Failure' },
  cancelled: { icon: Ban, color: 'gray', label: 'Cancelled' },
};

export const TerminalNode = memo(function TerminalNode({
  id,
  data,
  selected,
}: NodeProps & { data: TerminalNodeData }) {
  const setSelectedNodeId = useWorkflowDesignerStore((s) => s.setSelectedNodeId);
  const config = terminalConfig[data.terminalType] ?? {
    icon: CircleStop,
    color: 'gray',
    label: 'End',
  };
  const Icon = config.icon;

  const borderColors = {
    green: selected ? 'border-green-500 shadow-[0_0_15px_rgba(34,197,94,0.3)]' : 'border-green-500/40 hover:border-green-500/60',
    red: selected ? 'border-red-500 shadow-[0_0_15px_rgba(239,68,68,0.3)]' : 'border-red-500/40 hover:border-red-500/60',
    gray: selected ? 'border-trunnion-slate-400 shadow-[0_0_15px_rgba(107,114,128,0.3)]' : 'border-trunnion-slate-500/40 hover:border-trunnion-slate-500/60',
  };

  const bgColors = {
    green: 'bg-green-500/10',
    red: 'bg-red-500/10',
    gray: 'bg-trunnion-slate-500/10',
  };

  const iconColors = {
    green: 'text-green-400',
    red: 'text-red-400',
    gray: 'text-trunnion-slate-400',
  };

  return (
    <div
      className={`min-w-45 rounded-xl border-2 bg-trunnion-slate-800 shadow-lg transition-all ${borderColors[config.color as keyof typeof borderColors]}`}
      onClick={() => setSelectedNodeId(id)}
    >
      <div
        className={`flex items-center justify-center gap-2 rounded-[10px] px-4 py-4 ${bgColors[config.color as keyof typeof bgColors]}`}
      >
        <Icon className={`h-5 w-5 ${iconColors[config.color as keyof typeof iconColors]}`} />
        <span className="text-sm font-semibold text-white">
          {data.label || config.label}
        </span>
      </div>

      <Handle
        type="target"
        position={Position.Top}
        className="!bg-trunnion-slate-800 !border-trunnion-slate-500"
      />
    </div>
  );
});
